<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfigurableTypeStore } from '../../stores/configurableTypeStore';
import ImportTypeDialog from './ImportTypeDialog.vue';
import TypeFileDrawer from './TypeFileDrawer.vue';
import SmokeTestResultsPanel from './SmokeTestResultsPanel.vue';
import type { TypeFile, TypeWithPendingState } from '../../stores/configurableTypeStore';

const KIND_FILENAMES: Record<string, string> = {
    INIT: '__init__.py',
    FILTERS: 'filters.py',
    MODELS: 'models.py',
    REFERENCE_UNITS: 'reference_units.py',
    REGISTER: 'register.py',
    REPO: 'repository.py',
    STRATEGIES: 'strategies.py',
    TOOLS: 'tools.py',
    TYPE: 'type.py',
    REFERENCES_VIEW: 'ReferencesView.js',
    RAW_VIEW: 'RawView.js',
};

const FRONTEND_KINDS = new Set(['REFERENCES_VIEW', 'RAW_VIEW']);

type ToastKind = 'success' | 'error';

const store = useConfigurableTypeStore();
const { t } = useI18n();

const showImportDialog = ref(false);
const openFile = ref<TypeFile | null>(null);
const showDeleteConfirm = ref(false);
const deleting = ref(false);

const toast = ref<{ message: string; kind: ToastKind } | null>(null);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function showToast(message: string, kind: ToastKind = 'success') {
    if (toastTimer) clearTimeout(toastTimer);
    toast.value = { message, kind };
    toastTimer = setTimeout(() => {
        toast.value = null;
    }, 4000);
}

onMounted(async () => {
    await Promise.all([store.loadTypes(), store.loadBuiltinTypes()]);
});

onUnmounted(() => {
    if (toastTimer) clearTimeout(toastTimer);
    store.reset();
});

// =========================================================================
// Configurable type handlers
// =========================================================================

async function selectType(typeId: string) {
    if (store.selectedTypeId === typeId) {
        store.clearSelection();
    } else {
        openFile.value = null;
        store.clearSmokeTestResults();
        await store.selectType(typeId);
    }
}

function openFileDrawer(file: TypeFile) {
    openFile.value = file;
}

function closeFileDrawer() {
    openFile.value = null;
}

function onToggleEnabled(typeId: string, currentEffectiveEnabled: boolean) {
    store.queueEnableChange(typeId, !currentEffectiveEnabled);
}

function requestDelete() {
    if (store.hasPendingChanges) {
        showToast(t('typeManagement.toast.pendingBeforeDelete'), 'error');
        return;
    }
    showDeleteConfirm.value = true;
}

async function confirmDelete() {
    const typeId = store.selectedTypeId;
    if (!typeId) return;
    deleting.value = true;
    const typeName = store.selectedTypeDetail?.name ?? typeId;
    showDeleteConfirm.value = false;
    try {
        const result = await store.deleteType(typeId);
        if (result.success) {
            openFile.value = null;
            showToast(t('typeManagement.toast.typeDeleted', { typeName }), 'success');
        } else {
            showToast(result.message ?? t('typeManagement.toast.deleteFailed'), 'error');
        }
    } finally {
        deleting.value = false;
    }
}

function cancelDelete() {
    showDeleteConfirm.value = false;
}

async function saveAll() {
    const result = await store.saveAll();
    if (result.success) {
        if (result.unpack_result) {
            const { written, skipped, errors } = result.unpack_result;
            const filesWritten = t('typeManagement.toast.filesWritten', { count: written }, written);
            const errorPart = errors
                ? t('typeManagement.toast.errorsSuffix', { count: errors }, errors)
                : '';
            showToast(
                t('typeManagement.toast.saved', { filesWritten, skipped, errorPart }),
                'success',
            );
        } else {
            showToast(t('typeManagement.toast.savedSimple'), 'success');
        }
    } else {
        showToast(result.message ?? t('typeManagement.toast.saveFailed'), 'error');
    }
}

function openImport() {
    showImportDialog.value = true;
}

function onImportClose() {
    showImportDialog.value = false;
}

async function runTypeSmokeTest() {
    const typeName = store.selectedTypeDetail?.name;
    if (!typeName) return;
    const result = await store.runTypeSmokeTest(typeName);
    if (!result.success) {
        showToast(result.message ?? t('typeManagement.toast.smokeTestFailed'), 'error');
    }
}

async function runFilterSmokeTest() {
    const typeName = store.selectedTypeDetail?.name;
    if (!typeName) return;
    const result = await store.runFilterSmokeTest(typeName);
    if (!result.success) {
        showToast(result.message ?? t('typeManagement.toast.filterSmokeTestFailed'), 'error');
    }
}

function dismissSmokeTest() {
    store.clearSmokeTestResults();
}

function listItemClass(type: TypeWithPendingState) {
    const classes = ['type-mgmt-list-item'];
    if (type.record_id === store.selectedTypeId) classes.push('selected');
    if (!type.effectiveEnabled) classes.push('disabled-type');
    if (type.hasPendingChange) classes.push('pending-change');
    return classes.join(' ');
}

function badgeText(type: TypeWithPendingState) {
    if (type.hasPendingChange) return t('typeManagement.badge.unsaved');
    return type.effectiveEnabled ? t('typeManagement.badge.enabled') : t('typeManagement.badge.disabled');
}

function badgeClass(type: TypeWithPendingState) {
    if (type.hasPendingChange) return 'type-item-badge unsaved';
    return type.effectiveEnabled ? 'type-item-badge' : 'type-item-badge disabled';
}

const backendFiles = computed(
    () =>
        store.selectedTypeDetail?.files.filter((f: TypeFile) => !FRONTEND_KINDS.has(f.kind)) || [],
);

const frontendFiles = computed(
    () => store.selectedTypeDetail?.files.filter((f: TypeFile) => FRONTEND_KINDS.has(f.kind)) || [],
);

// =========================================================================
// Built-in type handlers
// =========================================================================

function selectBuiltinType(typeName: string) {
    openFile.value = null;
    store.selectBuiltinType(typeName);
}

function builtinListItemClass(typeName: string) {
    const classes = ['type-mgmt-list-item'];
    if (typeName === store.selectedBuiltinTypeName) classes.push('selected');
    return classes.join(' ');
}

async function runBuiltinTypeSmokeTest() {
    const typeName = store.selectedBuiltinTypeName;
    if (!typeName) return;
    const result = await store.runBuiltinTypeSmokeTest(typeName);
    if (!result.success) {
        showToast(result.message ?? t('typeManagement.toast.smokeTestFailed'), 'error');
    }
}

async function runBuiltinFilterSmokeTest() {
    const typeName = store.selectedBuiltinTypeName;
    if (!typeName) return;
    const result = await store.runBuiltinFilterSmokeTest(typeName);
    if (!result.success) {
        showToast(result.message ?? t('typeManagement.toast.filterSmokeTestFailed'), 'error');
    }
}

function dismissBuiltinSmokeTest() {
    store.clearBuiltinSmokeTestResults();
}

/** True when either a configurable or built-in type is selected. */
const hasAnySelection = computed(
    () => !!store.selectedTypeId || !!store.selectedBuiltinTypeName,
);
</script>

<template>
    <div class="type-mgmt-page">
        <div class="type-mgmt-toolbar">
            <span class="type-mgmt-toolbar-title">{{ t('typeManagement.title') }}</span>
            <button
                class="btn btn-success"
                :disabled="!store.hasPendingChanges || store.saving"
                @click="saveAll"
            >
                {{ store.saving ? t('typeManagement.savingButton') : t('typeManagement.saveAll') }}
            </button>
        </div>

        <div class="type-mgmt-body">
            <div class="type-mgmt-list-panel" :class="{ 'detail-open': hasAnySelection }">
                <div class="type-mgmt-list">
                    <!-- Built-in Types Section -->
                    <div class="type-mgmt-section-header">{{ t('typeManagement.builtinTypes') }}</div>

                    <div
                        v-if="store.loadingBuiltinList"
                        style="
                            padding: 8px 16px;
                            color: var(--color-text-secondary);
                            font-size: 0.82rem;
                        "
                    >
                        {{ t('common.loading') }}
                    </div>

                    <div
                        v-else-if="store.builtinTypes.length === 0"
                        style="
                            padding: 8px 16px;
                            color: var(--color-text-secondary);
                            font-size: 0.82rem;
                        "
                    >
                        {{ t('typeManagement.noBuiltinTypes') }}
                    </div>

                    <div
                        v-for="bt in store.builtinTypes"
                        :key="'builtin-' + bt.name"
                        :class="builtinListItemClass(bt.name)"
                        @click="selectBuiltinType(bt.name)"
                    >
                        <span class="type-item-name">{{ bt.name }}</span>
                        <span class="type-item-badge builtin">{{ t('typeManagement.badge.builtin') }}</span>
                    </div>

                    <!-- Configurable Types Section -->
                    <div class="type-mgmt-section-header" style="margin-top: 4px">
                        {{ t('typeManagement.configurableTypes') }}
                    </div>

                    <div
                        v-if="store.loadingList"
                        style="
                            padding: 8px 16px;
                            color: var(--color-text-secondary);
                            font-size: 0.82rem;
                        "
                    >
                        {{ t('common.loading') }}
                    </div>

                    <div
                        v-else-if="store.typesWithPendingState.length === 0"
                        style="
                            padding: 8px 16px;
                            color: var(--color-text-secondary);
                            font-size: 0.82rem;
                        "
                    >
                        {{ t('typeManagement.noConfigurableTypes') }}
                    </div>

                    <div
                        v-for="type in store.typesWithPendingState"
                        :key="type.record_id"
                        :class="listItemClass(type)"
                        @click="selectType(type.record_id)"
                    >
                        <span class="type-item-name">{{ type.name }}</span>
                        <span :class="badgeClass(type)">{{ badgeText(type) }}</span>
                    </div>
                </div>

                <div class="type-mgmt-list-footer">
                    <button class="btn btn-secondary" @click="openImport">{{ t('typeManagement.import') }}</button>
                </div>
            </div>

            <div class="type-mgmt-detail-panel">
                <!-- No selection -->
                <div v-if="!hasAnySelection" class="type-mgmt-detail-empty">
                    {{ t('typeManagement.selectTypePrompt') }}
                </div>

                <!-- Built-in type detail -->
                <template v-else-if="store.selectedBuiltinTypeName">
                    <div class="type-detail-header">
                        <span class="type-detail-name">{{ store.selectedBuiltinTypeName }}</span>
                        <button
                            class="btn btn-sm"
                            style="margin-left: auto"
                            :disabled="store.builtinSmokeTestLoading"
                            @click="runBuiltinTypeSmokeTest"
                        >
                            {{ store.builtinSmokeTestLoading ? t('typeManagement.testingButton') : t('typeManagement.testType') }}
                        </button>
                        <button
                            class="btn btn-sm"
                            :disabled="store.builtinFilterSmokeTestLoading"
                            @click="runBuiltinFilterSmokeTest"
                        >
                            {{
                                store.builtinFilterSmokeTestLoading
                                    ? t('typeManagement.testingButton')
                                    : t('typeManagement.testFilters')
                            }}
                        </button>
                    </div>

                    <div class="type-detail-meta">
                        <span class="type-detail-meta-label">
                            {{ t('typeManagement.statusLabel') }} <strong>{{ t('typeManagement.statusBuiltin') }}</strong>
                            <span
                                style="
                                    margin-left: 6px;
                                    color: var(--color-text-secondary);
                                    font-size: 0.8rem;
                                "
                                >{{ t('typeManagement.alwaysEnabled') }}</span
                            >
                        </span>
                    </div>

                    <SmokeTestResultsPanel
                        v-if="store.builtinSmokeTestLoading || store.builtinSmokeTestResults"
                        :results="store.builtinSmokeTestResults"
                        :loading="store.builtinSmokeTestLoading"
                        :title="t('typeManagement.typeSmokeTestResults')"
                        @close="dismissBuiltinSmokeTest"
                    />

                    <SmokeTestResultsPanel
                        v-if="
                            store.builtinFilterSmokeTestLoading ||
                            store.builtinFilterSmokeTestResults
                        "
                        :results="store.builtinFilterSmokeTestResults"
                        :loading="store.builtinFilterSmokeTestLoading"
                        :title="t('typeManagement.filterSmokeTestResults')"
                        @close="dismissBuiltinSmokeTest"
                    />
                </template>

                <!-- Configurable type detail -->
                <template v-else-if="store.selectedTypeId">
                    <template v-if="store.loadingDetail">
                        <div style="color: var(--color-text-secondary); font-size: 0.85rem">
                            {{ t('common.loading') }}
                        </div>
                    </template>

                    <template v-else-if="store.selectedTypeDetail">
                        <div class="type-detail-header">
                            <span class="type-detail-name">{{
                                store.selectedTypeDetail.name
                            }}</span>
                            <button
                                class="btn btn-sm"
                                style="margin-left: auto"
                                :disabled="store.smokeTestLoading"
                                @click="runTypeSmokeTest"
                            >
                                {{ store.smokeTestLoading ? t('typeManagement.testingButton') : t('typeManagement.testType') }}
                            </button>
                            <button
                                class="btn btn-sm"
                                :disabled="store.filterSmokeTestLoading"
                                @click="runFilterSmokeTest"
                            >
                                {{
                                    store.filterSmokeTestLoading ? t('typeManagement.testingButton') : t('typeManagement.testFilters')
                                }}
                            </button>
                            <button
                                class="btn btn-danger btn-sm"
                                :disabled="deleting"
                                @click="requestDelete"
                            >
                                {{ t('common.delete') }}
                            </button>
                        </div>

                        <div class="type-detail-meta">
                            <span class="type-detail-meta-label">
                                {{ t('typeManagement.statusLabel') }}
                                <strong>{{
                                    store.selectedType?.effectiveEnabled ? t('typeManagement.statusEnabled') : t('typeManagement.statusDisabled')
                                }}</strong>
                                <span
                                    v-if="store.selectedType?.hasPendingChange"
                                    style="
                                        margin-left: 6px;
                                        color: var(--color-error);
                                        font-size: 0.8rem;
                                    "
                                    >{{ t('typeManagement.unsavedSuffix') }}</span
                                >
                            </span>
                            <div class="type-detail-toggle">
                                <label :for="'toggle-' + store.selectedTypeDetail.record_id">
                                    {{
                                        store.selectedType?.effectiveEnabled ? t('typeManagement.disable') : t('typeManagement.enable')
                                    }}
                                </label>
                                <input
                                    :id="'toggle-' + store.selectedTypeDetail.record_id"
                                    type="checkbox"
                                    :checked="store.selectedType?.effectiveEnabled"
                                    @change="
                                        onToggleEnabled(
                                            store.selectedTypeDetail.record_id,
                                            !!store.selectedType?.effectiveEnabled,
                                        )
                                    "
                                />
                            </div>
                        </div>

                        <div class="type-files-section-title">{{ t('typeManagement.backendFiles') }}</div>

                        <div
                            v-for="file in backendFiles"
                            :key="file.record_id"
                            class="type-file-item"
                            @click="openFileDrawer(file)"
                        >
                            <div class="type-file-item-header">
                                <span class="type-file-kind">{{ file.kind }}</span>
                                <span class="type-file-name">{{
                                    KIND_FILENAMES[file.kind] ?? file.kind
                                }}</span>
                                <span class="material-icons type-file-open-icon"
                                    >chevron_right</span
                                >
                            </div>
                        </div>

                        <div class="type-files-section-title" style="margin-top: 16px">
                            {{ t('typeManagement.frontendFiles') }}
                        </div>

                        <div
                            v-for="file in frontendFiles"
                            :key="file.record_id"
                            class="type-file-item"
                            @click="openFileDrawer(file)"
                        >
                            <div class="type-file-item-header">
                                <span class="type-file-kind">{{ file.kind }}</span>
                                <span class="type-file-name">{{
                                    KIND_FILENAMES[file.kind] ?? file.kind
                                }}</span>
                                <span class="material-icons type-file-open-icon"
                                    >chevron_right</span
                                >
                            </div>
                        </div>

                        <SmokeTestResultsPanel
                            v-if="store.smokeTestLoading || store.smokeTestResults"
                            :results="store.smokeTestResults"
                            :loading="store.smokeTestLoading"
                            :title="t('typeManagement.typeSmokeTestResults')"
                            @close="dismissSmokeTest"
                        />

                        <SmokeTestResultsPanel
                            v-if="store.filterSmokeTestLoading || store.filterSmokeTestResults"
                            :results="store.filterSmokeTestResults"
                            :loading="store.filterSmokeTestLoading"
                            :title="t('typeManagement.filterSmokeTestResults')"
                            @close="dismissSmokeTest"
                        />
                    </template>
                </template>
            </div>
        </div>

        <TypeFileDrawer :file="openFile" @close="closeFileDrawer" />

        <ImportTypeDialog v-if="showImportDialog" @close="onImportClose" />

        <div v-if="showDeleteConfirm" class="type-mgmt-modal-backdrop" @click.self="cancelDelete">
            <div class="type-mgmt-modal">
                <div class="type-mgmt-modal-title">{{ t('typeManagement.deleteModal.title') }}</div>
                <div class="type-mgmt-modal-body">
                    {{ t('typeManagement.deleteModal.confirmPrefix') }}
                    <strong>{{ store.selectedTypeDetail?.name }}</strong
                    >{{ t('typeManagement.deleteModal.confirmSuffix') }}
                </div>
                <div class="type-mgmt-modal-footer">
                    <button class="btn btn-secondary" @click="cancelDelete">{{ t('common.cancel') }}</button>
                    <button class="btn btn-danger" :disabled="deleting" @click="confirmDelete">
                        {{ deleting ? t('typeManagement.deletingButton') : t('common.delete') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="toast" :class="['type-mgmt-toast', toast.kind]">
            {{ toast.message }}
        </div>
    </div>
</template>
